import { SportsModeServiceRepository } from '@/__core__/repository/sports-mode-service.repository'
import { defineStore } from 'pinia'
import { shouldScheduleRefresh } from './LiveScoreboard.helpers'
import { extendDictionary, initLocalization, loc } from '@/__core__/localization/localization'
import { allSettled } from '@/__core__/utils/fetch'

/** @typedef {{ eventId: string, competitorId: string, competitor2Id: string, sportType: string, languageIdentifier: string, region: string }} Payload */
export const useLiveScoreboardStore = defineStore('livescoreboard', {
  state: () => ({
    periodScores: [],
    header: null,
    scorers: null,
    venue: null,
    previousMeetings: null,
    langId: '',
    drfRetryCounter: 0
  }),

  getters: {
    getShouldStopRefresh() {
      return this.drfRetryCounter >= 10
    }
  },
  actions: {
    getShouldCallApi() {
      return shouldScheduleRefresh(this.header)
    },
    /** @param {Payload} payload */
    async fetchLiveScoreboardData(payload) {
      try {
        const [, responseLocalization, responseLiveScoreboard] = await allSettled([
          initLocalization(payload.languageIdentifier),
          SportsModeServiceRepository.Localization.get({ languageIdentifier: payload.languageIdentifier, widgetId: 'statistics' }),
          SportsModeServiceRepository.LiveScoreboard.get(payload)
        ])

        if (!responseLocalization) {
          throw new Error('Localization data not found')
        }
        extendDictionary(responseLocalization.data, payload.languageIdentifier)
        if (!responseLiveScoreboard) {
          throw new Error('LiveScoreboard data not found')
        }

        this.storeResponse(responseLiveScoreboard.data, payload.languageIdentifier)

        return {
          header: this.header,
          scorers: this.scorers,
          previousMeetings: this.previousMeetings,
          venue: this.venue,
          periodScores: this.periodScores
        }
      } catch (error) {
        throw new Error(`[LiveScoreboard.store, fetchLiveScoreboardData]: ${error.message}`)
      }
    },
    /** @param {Payload} payload */
    async scheduleLiveScoreboardRefresh(payload) {
      try {
        const response = await SportsModeServiceRepository.LiveScoreboard.get(payload)
        this.storeResponse(response.data, payload.languageIdentifier)
      } catch {
        this.drfRetryCounter++
      }
    },
    storeResponse(response, languageIdentifier) {
      const { teams, round, events = [], competition, isGameLive, matchStatus, startTime, periodScores = [], status, previousMeetings, venue } = response
      let home = teams.find(team => team.qualifier === 'home')
      let away = teams.find(team => team.qualifier === 'away')
      let penaltiesScore = periodScores.find(period => period.type === 'penalties')
      const startDate = new Date(startTime) // Convert to Date object
      const currentDate = new Date() // Get the current date and time
      let competitionLabel = competition.name

      if (round.number) {
        competitionLabel += `, ${loc('sm_round_rs', languageIdentifier)} ${round.number}`
      } else if (round.name) {
        const labelKey = `sm_${round.name}`
        competitionLabel += `, ${loc(labelKey, languageIdentifier)}`
      } else {
        // do nothing
      }

      this.header = {
        matchStatus,
        home,
        away,
        startTime,
        isGameLive,
        competitionLabel,
        status,
        upcoming: startDate > currentDate,
        penaltiesScore: penaltiesScore && `${penaltiesScore.home}:${penaltiesScore.away}`
      }

      this.scorers = events
      this.venue = venue
      this.previousMeetings = previousMeetings
      this.langId = languageIdentifier
      this.periodScores = periodScores
    }
  }
})
